import React, {useReducer, useEffect} from 'react'
import gql from 'graphql-tag'
import {Radio, Checkbox, Spin, notification} from 'antd'
import {useLazyQuery} from '@apollo/react-hooks'

import {getProfile} from 'lib/photoProfiles'
import {getImageTitle, sortPhotos} from 'lib/imageUtils'
import PhotoGallery from 'components/shared/PhotoGallery'
import SelectablePhotoThumb from 'components/shared/SelectablePhotoThumb'
import Button from 'components/shared/Button'

const fetchEnhancedAssetsQuery = gql`
  query FetchEnhancedAssetsQuery($productId: ID!, $profile: String) {
    product(id: $productId) {
      id
      ...on AssetsBasedProduct {
        assets {
          edges {
            node {
              id
              ...on PhotoAsset {
                customProfileAvailable
                customProfilePreview(profile: $profile) {
                  id
                  profile
                  image {
                    url
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

function sortPhotosSafe(photos, photoOrder) {
  if (photoOrder) {
    return sortPhotos(photos, photoOrder)
  }

  return photos
}

function createPhotosFromAssets(assets, previews, defaultProfile, photoOrder) {
  const photos = []
  assets.forEach(asset => {
    const photo = {
      id: asset.id,
      editable: false,
      title: getImageTitle(asset.image),
      image: {
        ...asset.image
      }
    }
    if (asset.customProfileAvailable) {
      photo.editable = true
      photo.profile = asset.profile || defaultProfile
      if (asset.id in previews) {
        photo.profile = previews[asset.id].profile
        photo.image.url = previews[asset.id].image.url
      }
    }
    photos.push(photo)
  })

  return sortPhotosSafe(photos, photoOrder)
}

function getPhotoCategories(photos) {
  const categories = {}
  let index = 0
  photos.forEach(photo => {
    const label = photo.image.labels[0] || 'Uten navn'
    if (!categories[label]) {
      categories[label] = []
    }
    photo.index = index
    categories[label].push(photo)
    index += 1
  })

  return categories
}

const initialState = {
  photoPreviewIndex: null,
  selectedAssetIds: {},
  assetsToBeUpdated: {},
  assetPreviews: {},
}
function reducer(state, action) {
  switch (action.type) {
    case 'previewPhoto':
      return {...state, photoPreviewIndex: action.index}

    case 'closeGallery':
      return {...state, photoPreviewIndex: null}

    case 'selectPhotos':
      const selectedAssetIds = {...state.selectedAssetIds}
      action.photos.forEach(photo => {
        if (photo.editable && action.selected) {
          selectedAssetIds[photo.id] = true
        } else {
          delete selectedAssetIds[photo.id]
        }
      })
      return {...state, selectedAssetIds}

    case 'setAssetsToBeUpdated':
      const assetsToBeUpdated = action.assetIds.reduce((acc, id) => {
        acc[id] = true
        return acc
      }, {})
      return {...state, assetsToBeUpdated}

    case 'setPreviewForAsset':
      const assetPreviews = {...state.assetPreviews}
      action.assets.forEach(asset => {
        if (state.assetsToBeUpdated[asset.id]) {
          assetPreviews[asset.id] = {...asset.customProfilePreview}
        }
      })
      return {...state, assetPreviews}

    default:
      throw new Error(`Do not know action: ${action.type}`)
  }
}

export default function ChangeCustomProfile({viewer, product, setCustomProfile, isSubmitting, setGoBackToProduct}) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [getPreviews, {loading, data: previewData}] = useLazyQuery(fetchEnhancedAssetsQuery, {
    onError: () => {
      notification['error']({
        message: 'Feil!',
        description: 'Kunne ikke forhåndsvise bilder på grunn av en feil. Forsøk på nytt senere.',
      })
    }
  })
  useEffect(() => {
    if (previewData && Object.keys(state.assetsToBeUpdated).length) {
      const assets = previewData.product.assets.edges.map(({node}) => (node))
      dispatch({type: 'setPreviewForAsset', assets})
    }
  }, [previewData, state.assetsToBeUpdated])

  const photoOrder = viewer.photoOrderPreference.edges.map(edge => edge.node.name)
  const assets = product.assets.edges.map(edge => edge.node)
  const defaultProfile = product.customProfile !== 'custom' ? product.customProfile : null
  const somethingSelected = Object.values(state.selectedAssetIds).length

  const photos = createPhotosFromAssets(assets, state.assetPreviews, defaultProfile, photoOrder)
  const categories = getPhotoCategories(photos)

  const existingAssetProfileSelection = assets.reduce((acc, asset) => {
    if (asset.customProfileAvailable) {
      acc[asset.id] = {
        profile: asset.profile || defaultProfile || null
      }
    }
    return acc
  }, {})

  function submitAllowed() {
    if (isSubmitting) {
      return false
    }
    const existingSelection = existingAssetProfileSelection
    return Object
      .entries(state.assetPreviews)
      .some(([assetId, preview]) => (existingSelection[assetId].profile !== preview.profile))
  }

  function getPhotoTitle(index) {
    return photos[index].title
  }

  function allPhotosChecked(photos) {
    return photos.every(({id, editable}) => (state.selectedAssetIds[id] || !editable))
  }

  function handleProfileChange(profile, assetIds) {
    dispatch({type: 'setAssetsToBeUpdated', assetIds})
    getPreviews({variables: {profile, productId: product.id}})
  }

  function submitProfile() {
    if (!submitAllowed()) {
      return
    }
    const assetProfiles = []
    const finalSelection = {...existingAssetProfileSelection, ...state.assetPreviews}
    Object.entries(finalSelection).forEach(([id, {profile}]) => {
      assetProfiles.push({id, profile})
    })

    setCustomProfile({
      variables: {
        input: {
          productId: product.id,
          profile: 'custom',
          assets: assetProfiles,
        }
      }
    })
  }

  function getPhotoCaption(index) {
    const photo = photos[index]
    return (
      <div className="product-custom-profile-gallery-caption">
        {photo.editable && (
          <Radio.Group
            name="profile"
            value={photo.profile || ''}
            onChange={(e) => handleProfileChange(e.target.value || null, [photo.id])}
          >
            <Radio value="">
              {getProfile('').name}
            </Radio>
            {viewer.customer.customProfiles.map(profile => (
              <Radio key={profile} value={profile}>
                {getProfile(profile).name}
              </Radio>
            ))}
          </Radio.Group>
        )}

        {!photo.editable && (
          <>Cannot apply filter</>
        )}
      </div>
    )
  }

  return (
    <>
      <div className="profiles">
        <Button
          disabled={!somethingSelected}
          onClick={() => handleProfileChange(null, Object.keys(state.selectedAssetIds))}
        >
          {getProfile('').name}
        </Button>

        {viewer.customer.customProfiles.map(profile => (
          <Button
            key={profile}
            disabled={!somethingSelected}
            onClick={() => handleProfileChange(profile, Object.keys(state.selectedAssetIds))}
          >
            {getProfile(profile).name}
          </Button>
        ))}
      </div>

      {loading && (
        <div className="loading">
          <Spin />
        </div>
      )}

      {!loading && Object.keys(categories).map((category) => (
        <div key={category} className="photos-groups">
          <div className="check-all">
            <Checkbox
              checked={allPhotosChecked(categories[category])}
              onChange={(e) => dispatch({type: 'selectPhotos', photos: categories[category], selected: e.target.checked})}
            >
              {category}
            </Checkbox>
          </div>
          <SelectablePhotoThumb.Wrapper>
            {categories[category].map(photo => (
              <SelectablePhotoThumb
                key={photo.id}
                photo={photo}
                onChange={(selected) => dispatch({type: 'selectPhotos', photos: [photo], selected})}
                selected={state.selectedAssetIds[photo.id]}
                isSelectable={photo.editable}
                title={photo.title}
                onClick={() => dispatch({type: 'previewPhoto', index: photo.index})}
                label={photo.editable ? getProfile(photo.profile || '').name : 'Kan ikke endres'}
              />
            ))}
          </SelectablePhotoThumb.Wrapper>
        </div>
      ))}

      {!loading && (
        <div className="actions">
          <Button
            secondary
            type="navigation"
            onClick={setGoBackToProduct}
          >
            Tilbake
          </Button>
          <Button
            type="action"
            loading={isSubmitting}
            disabled={!submitAllowed()}
            onClick={() => submitProfile()}
          >
            Generér
          </Button>
        </div>
      )}

      {state.photoPreviewIndex !== null &&
        <PhotoGallery
          loading={loading}
          images={photos.map(p => p.image.url)}
          initialImageIndex={state.photoPreviewIndex}
          onCloseRequest={() => dispatch({type: 'closeGallery'})}
          onRenderTitle={getPhotoTitle}
          onRenderCaption={getPhotoCaption}
        />
      }
    </>
  )
}
